import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { Banner } from '@/components/ui/banner'
import { Button } from '@/components/ui/button'
import { YesNo } from '@/components/ui/button-group'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Field, FieldDescription, FieldError, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { SkeletonRows } from '@/components/ui/skeleton'
import {
  type ComplianceScope,
  type ConfirmedScope,
  type ScopeConflict,
  useComplianceScope,
  useConfirmComplianceScope,
} from '@/models/vault'
import { useVault } from './VaultContext'

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const FACT_LABELS: Record<string, string> = {
  power_units: 'power units',
  drivers_count: 'drivers',
  interstate: 'interstate operation',
  hazmat: 'hazmat',
  legal_name: 'legal name',
  entity_type: 'entity type',
  operating_status: 'operating status',
}

function describe(value: unknown): string {
  if (value === true) return 'yes'
  if (value === false) return 'no'
  return String(value ?? '—')
}

function conflictLine(conflict: ScopeConflict): string {
  const label = FACT_LABELS[conflict.field] ?? conflict.field
  return `FMCSA lists ${describe(conflict.fmcsa)} for ${label}; we have ${describe(conflict.ours)} on record.`
}

/**
 * The vault's first screen. The fleet's DOT is looked up, the business owners are
 * the contacts and the roster is the count, so the carrier answers only what the
 * lookup cannot be trusted on — interstate, hazmat, and where to write — and one
 * button signs the scope off and starts the chase.
 */
export function ScopeSetup({
  initial,
  onDone,
  onCancel,
}: {
  /** A scope confirmed before, when the carrier is editing it. */
  initial?: ComplianceScope | null
  onDone: (confirmed: ConfirmedScope) => void
  onCancel?: () => void
}) {
  const { orgId } = useVault()
  const { draft, isLoading } = useComplianceScope(orgId)
  const confirm = useConfirmComplianceScope(orgId)
  const [interstate, setInterstate] = useState<boolean | null>(initial?.interstate ?? null)
  const [hazmat, setHazmat] = useState<boolean | null>(initial?.hazmat ?? null)
  const [emails, setEmails] = useState('')
  const [seeded, setSeeded] = useState(false)

  // The draft seeds the answers once; what the carrier picks after that stands.
  useEffect(() => {
    if (!draft || seeded) return
    setSeeded(true)
    if (interstate === null) setInterstate(draft.profile.interstate)
    if (hazmat === null) setHazmat(draft.profile.hazmat)
    if (!emails) setEmails(draft.notificationEmails.join(', '))
  }, [draft, seeded, interstate, hazmat, emails])

  const addresses = emails
    .split(',')
    .map((address) => address.trim())
    .filter(Boolean)
  const emailsValid = addresses.length > 0 && addresses.every((address) => EMAIL.test(address))

  const submit = () => {
    confirm.mutate(
      { interstate, hazmat, notificationEmails: addresses },
      {
        onSuccess: (result) => {
          toast.success('Scope confirmed. We’ve asked for the first documents.')
          onDone(result)
        },
        onError: () => toast.error('Could not save the scope. Try again.'),
      }
    )
  }

  const editing = !!initial?.dateConfirmed

  return (
    <Card>
      <CardHeader>
        <CardTitle>{editing ? 'Your compliance scope' : 'Set up your compliance vault'}</CardTitle>
        <CardDescription className="leading-relaxed">
          We look your fleet&rsquo;s USDOT number up, draft what needs to be on file for the drivers and
          vehicles on your roster, and you confirm it. The vault writes to your business owners.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        {isLoading || !draft ? (
          <SkeletonRows />
        ) : !draft.hasDot ? (
          <Banner variant="warning">
            Your fleet has no USDOT number yet. Add it under Fleet details and the vault will look the
            operation up.
          </Banner>
        ) : (
          <div className="flex flex-col gap-6 text-sm leading-relaxed">
            {/* The name on the left, the two answers on the right; a phone wraps them under. */}
            <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between lg:gap-8">
              <div className="flex flex-col gap-1">
                <span className="text-muted-foreground text-xs font-medium uppercase">
                  {draft.found ? 'What we found' : 'What we have on record'}
                </span>
                <h3 className="text-lg font-semibold">{draft.profile.legalName || 'Your operation'}</h3>
                <p className="text-muted-foreground">DOT {draft.profile.dotNumber}</p>
              </div>
              <div className="flex flex-wrap gap-6 lg:shrink-0 lg:justify-end">
                <Field className="w-auto" role="group" aria-label="Interstate">
                  <FieldLabel>Do you run interstate?</FieldLabel>
                  <YesNo value={interstate} onChange={setInterstate} className="w-64" />
                </Field>
                <Field className="w-auto" role="group" aria-label="Hazmat">
                  <FieldLabel>Do you haul hazmat?</FieldLabel>
                  <YesNo value={hazmat} onChange={setHazmat} className="w-64" />
                </Field>
              </div>
            </div>

            <p>{draft.summary}</p>

            <ul className="text-muted-foreground list-disc space-y-2 pl-4">
              {draft.profile.cargo.length > 0 && <li>Cargo on record: {draft.profile.cargo.join(', ')}.</li>}
              {draft.conflicts.map((conflict) => (
                <li key={conflict.field}>{conflictLine(conflict)}</li>
              ))}
              {draft.notes.map((note) => (
                <li key={note}>{note}</li>
              ))}
            </ul>

            <Field data-invalid={emails.length > 0 && !emailsValid}>
              <FieldLabel htmlFor="vault-emails">
                Where should we send what is missing and expiring?
              </FieldLabel>
              <Input
                id="vault-emails"
                value={emails}
                onChange={(event) => setEmails(event.target.value)}
                aria-invalid={emails.length > 0 && !emailsValid}
                placeholder="owner@yourfleet.com"
              />
              {emails.length > 0 && !emailsValid ? (
                <FieldError>That does not look like an email address.</FieldError>
              ) : (
                <FieldDescription>
                  Defaults to your business owners. Separate several addresses with commas.
                </FieldDescription>
              )}
            </Field>
          </div>
        )}
      </CardContent>
      <CardFooter className="flex flex-wrap items-center gap-3">
        <Button
          onClick={submit}
          loading={confirm.isPending ? 'Saving…' : undefined}
          disabled={!draft?.hasDot || interstate === null || hazmat === null || !emailsValid}
        >
          {editing ? 'Save scope' : 'Confirm and start collecting'}
        </Button>
        {onCancel && (
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </CardFooter>
    </Card>
  )
}
